import ExcelJS from 'exceljs';

export type ImportTemplateType = 'companies' | 'sales_reps' | 'parties' | 'items' | 'prices';

interface TemplateConfig {
  filename: string;
  sheetName: string;
  columns: { header: string; key: string; width: number; example: any }[];
}

const TEMPLATE_CONFIGS: Record<ImportTemplateType, TemplateConfig> = {
  companies: {
    filename: 'Companies_Import_Template.xlsx',
    sheetName: 'Companies',
    columns: [
      { header: 'Code', key: 'code', width: 14, example: 'SKI' },
      { header: 'Name', key: 'name', width: 32, example: 'Sai Krishna Industries' },
      { header: 'GST', key: 'gst', width: 22, example: '03ACQFS9127A1Z6' },
      { header: 'Address', key: 'address', width: 45, example: 'B-XXIX-536/35-B/A, Sua Road, Giaspura, Ludhiana' },
      { header: 'Phone', key: 'phone', width: 25, example: '0161-5034059, 93167-53530' },
      { header: 'Email', key: 'email', width: 35, example: 'sutrantechnoconveyors@gmail.com' },
      { header: 'Bank Name', key: 'bank_name', width: 26, example: 'Bank of Baroda, Ludhiana' },
      { header: 'Account Number', key: 'account_number', width: 22, example: '78450500000073' },
      { header: 'IFSC Code', key: 'ifsc_code', width: 18, example: 'BARB0VJMLUD' },
      { header: 'Signatory', key: 'signatory', width: 20, example: 'Arjun Bhalla' },
      { header: 'HSN Codes', key: 'hsn_codes', width: 28, example: '87169090, 84483990, 84482000' },
    ],
  },
  sales_reps: {
    filename: 'SalesReps_Import_Template.xlsx',
    sheetName: 'SalesReps',
    columns: [
      { header: 'Code', key: 'code', width: 14, example: 'AB' },
      { header: 'Name', key: 'name', width: 28, example: 'Arjun Bhalla' },
      { header: 'Role', key: 'role', width: 18, example: 'SMGR' },
      { header: 'Company IDs', key: 'company_ids', width: 20, example: '1' },
    ],
  },
  parties: {
    filename: 'Parties_Customer_Import_Template.xlsx',
    sheetName: 'Parties',
    columns: [
      { header: 'Name', key: 'name', width: 36, example: 'Acme Spinning Mills Ltd' },
      { header: 'Address 1', key: 'address1', width: 35, example: '10 Km Stone, Industrial Focal Point' },
      { header: 'Address 2', key: 'address2', width: 25, example: 'Near Verka Chowk' },
      { header: 'Address 3', key: 'address3', width: 25, example: 'Ludhiana, Punjab - 141010' },
      { header: 'Contact Person', key: 'contact_person', width: 22, example: 'Mr. R. K. Sharma' },
      { header: 'Phone Nos', key: 'phone', width: 22, example: '98765-43210' },
      { header: 'Email', key: 'email', width: 30, example: 'purchase@acmespinning.com' },
      { header: 'GST', key: 'gst', width: 22, example: '03ABCDE1234F1Z5' },
      { header: 'Company ID', key: 'company_id', width: 16, example: 1 },
    ],
  },
  items: {
    filename: 'Product_Catalog_Import_Template.xlsx',
    sheetName: 'Products',
    columns: [
      { header: 'Code', key: 'code', width: 30, example: 'SF1/SW/PP/F/615' },
      { header: 'Description', key: 'description', width: 45, example: 'Spinfine (SF 1) Swivel Castor 6" x 1 1/2"' },
      { header: 'Castor Type', key: 'castor_type', width: 18, example: 'SF1' },
      { header: 'Wheel Type', key: 'wheel_type', width: 16, example: 'SW' },
      { header: 'Wheel Material', key: 'wheel_material', width: 18, example: 'PP' },
      { header: 'Size', key: 'size', width: 16, example: '6 x 1 1/2' },
      { header: 'Bearing', key: 'bearing', width: 14, example: '6203' },
      { header: 'Circlip', key: 'circlip', width: 12, example: 'CIR' },
      { header: 'Bush', key: 'bush', width: 12, example: 'GB' },
      { header: 'Thread Guard', key: 'thread_guard', width: 16, example: 'PTG' },
      { header: 'Nut Bolt', key: 'nut_bolt', width: 14, example: 'NB' },
      { header: 'Base Price', key: 'base_price', width: 16, example: 910 },
    ],
  },
  prices: {
    filename: 'Price_List_Import_Template.xlsx',
    sheetName: 'PriceList',
    columns: [
      { header: 'Category', key: 'category', width: 18, example: 'Castors' },
      { header: 'Item Code', key: 'item_code', width: 28, example: 'MSFB/SW/4 x 1 1/4' },
      { header: 'Item Name', key: 'item_name', width: 22, example: 'MSFB' },
      { header: 'Size', key: 'size', width: 18, example: '4 x 1 1/4' },
      { header: 'Price', key: 'price', width: 16, example: 310 },
      { header: 'Effective Date', key: 'effective_date', width: 18, example: '2026-01-01' },
    ],
  },
};

export async function downloadExcelTemplate(type: ImportTemplateType): Promise<void> {
  const config = TEMPLATE_CONFIGS[type];
  if (!config) return;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Sai Krishna Industries';
  workbook.created = new Date();

  const sheet = workbook.addWorksheet(config.sheetName, {
    views: [{ showGridLines: true }],
  });

  sheet.columns = config.columns.map((col) => ({
    header: col.header,
    key: col.key,
    width: col.width,
  }));

  // Style Header Row
  const headerRow = sheet.getRow(1);
  headerRow.height = 28;
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF1E3A8A' }, // Navy
    };
    cell.font = {
      name: 'Calibri',
      size: 10.5,
      bold: true,
      color: { argb: 'FFFFFFFF' },
    };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FF93C5FD' } },
      bottom: { style: 'medium', color: { argb: 'FF1E40AF' } },
      left: { style: 'thin', color: { argb: 'FF93C5FD' } },
      right: { style: 'thin', color: { argb: 'FF93C5FD' } },
    };
  });

  // Add 2 Example Sample Rows
  const sampleRow1: Record<string, any> = {};
  config.columns.forEach((col) => {
    sampleRow1[col.key] = col.example;
  });
  const row1 = sheet.addRow(sampleRow1);
  row1.height = 20;

  // Add a second descriptive example row
  const sampleRow2: Record<string, any> = {};
  config.columns.forEach((col) => {
    if (typeof col.example === 'number') {
      sampleRow2[col.key] = col.example * 1.1;
    } else if (String(col.example).includes('SKI')) {
      sampleRow2[col.key] = 'SPPL/GGN';
    } else if (String(col.example).includes('Sai Krishna')) {
      sampleRow2[col.key] = 'Sutran Polymers Pvt. Ltd.';
    } else if (String(col.example).includes('MSFB')) {
      sampleRow2[col.key] = col.example.replace('MSFB', 'PS/EL');
    } else {
      sampleRow2[col.key] = col.example;
    }
  });
  const row2 = sheet.addRow(sampleRow2);
  row2.height = 20;

  [row1, row2].forEach((row) => {
    row.eachCell((cell) => {
      cell.font = { name: 'Calibri', size: 9.5 };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFE5E7EB' } },
        bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } },
        left: { style: 'thin', color: { argb: 'FFE5E7EB' } },
        right: { style: 'thin', color: { argb: 'FFE5E7EB' } },
      };
      cell.alignment = { vertical: 'middle' };
    });
  });

  // Trigger browser download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = config.filename;
  anchor.click();
  window.URL.revokeObjectURL(url);
}
